"use client";

import {
	AudioLines,
	Film,
	ImagePlus,
	MessageSquare,
	PenTool,
	Users,
} from "lucide-react";
import Link from "next/link";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { cn } from "@/lib/utils";

import { useI18n } from "@llmgateway/i18n/react";
import { getProviderIcon } from "@llmgateway/shared/components";

import type { LucideIcon } from "lucide-react";

interface AuthDialogProps {
	open: boolean;
	returnUrl?: string;
	title?: string;
	description?: string;
}

const PROVIDER_LOGOS = [
	"openai",
	"anthropic",
	"google-ai-studio",
	"mistral",
	"xai",
	"deepseek",
] as const;

interface Feature {
	icon: LucideIcon;
	title: string;
	description: string;
}

export function AuthDialog({
	open,
	returnUrl,
	title,
	description,
}: AuthDialogProps) {
	const { t } = useI18n();

	if (!open) {
		return null;
	}

	const effectiveTitle = title ?? t("authDialog.title");
	const effectiveDescription = description ?? t("authDialog.description");
	const features: Feature[] = [
		{
			icon: MessageSquare,
			title: t("playground.chat"),
			description: t("authDialog.chatDescription"),
		},
		{
			icon: ImagePlus,
			title: t("playground.imageStudio"),
			description: t("authDialog.imageDescription"),
		},
		{
			icon: Film,
			title: t("playground.videoStudio"),
			description: t("authDialog.videoDescription"),
		},
		{
			icon: AudioLines,
			title: t("playground.audioStudio"),
			description: t("authDialog.audioDescription"),
		},
		{
			icon: PenTool,
			title: t("playground.canvas"),
			description: t("authDialog.canvasDescription"),
		},
		{
			icon: Users,
			title: t("playground.groupChat"),
			description: t("authDialog.groupChatDescription"),
		},
	];
	const highlights = [
		t("authDialog.highlightHistory"),
		t("authDialog.highlightPlans"),
	];

	const loginUrl = returnUrl
		? `/login?returnUrl=${encodeURIComponent(returnUrl)}`
		: "/login";
	const signupUrl = returnUrl
		? `/signup?returnUrl=${encodeURIComponent(returnUrl)}`
		: "/signup";

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background/95 p-4 backdrop-blur-sm">
			<div className="my-8 w-full max-w-lg rounded-2xl border bg-card p-8 shadow-2xl">
				<div className="flex items-center gap-2">
					<Logo className="size-6" />
					<span className="text-base font-semibold">doteb</span>
					<Badge>{t("playground.chat")}</Badge>
				</div>

				<h1 className="mt-6 text-2xl font-semibold leading-tight tracking-tight">
					{effectiveTitle}
				</h1>
				<p className="mt-3 text-sm text-muted-foreground">
					{effectiveDescription}
				</p>

				<div className="mt-6 flex items-center gap-3">
					{PROVIDER_LOGOS.map((provider) => {
						const Icon = getProviderIcon(provider);
						return (
							<Icon
								key={provider}
								className="size-6 shrink-0 text-muted-foreground/70 dark:text-white/80"
								aria-hidden
							/>
						);
					})}
				</div>

				<div className="mt-6 grid gap-2.5 sm:grid-cols-2">
					{features.map((feature) => {
						const Icon = feature.icon;
						return (
							<div
								key={feature.title}
								className="flex items-start gap-3 rounded-xl border bg-background/40 p-3"
							>
								<span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
									<Icon className="size-4" aria-hidden />
								</span>
								<div className="min-w-0">
									<div className="text-sm font-medium leading-tight">
										{feature.title}
									</div>
									<p className="mt-0.5 text-xs text-muted-foreground">
										{feature.description}
									</p>
								</div>
							</div>
						);
					})}
				</div>

				<ul className="mt-5 space-y-2.5">
					{highlights.map((highlight) => (
						<li
							key={highlight}
							className="flex items-start gap-2.5 text-sm text-foreground/80"
						>
							<CheckIcon className="mt-0.5 size-4 shrink-0 text-primary" />
							<span>{highlight}</span>
						</li>
					))}
				</ul>

				<div className="mt-8 flex flex-col gap-3">
					<Button size="lg" className="w-full" asChild>
						<Link href={signupUrl}>{t("auth.startFree")}</Link>
					</Button>
					<p className="text-center text-sm text-muted-foreground">
						{t("auth.alreadyHaveAccountQuestion")}{" "}
						<Link
							href={loginUrl}
							className={cn(
								"font-medium text-foreground underline-offset-4 hover:underline",
							)}
						>
							{t("auth.signIn")}
						</Link>
					</p>
				</div>
			</div>
		</div>
	);
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			viewBox="0 0 20 20"
			fill="currentColor"
			aria-hidden
		>
			<path
				fillRule="evenodd"
				d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.5 7.6a1 1 0 0 1-1.42.006l-3.5-3.5a1 1 0 1 1 1.414-1.414l2.79 2.79 6.796-6.886a1 1 0 0 1 1.414-.006Z"
				clipRule="evenodd"
			/>
		</svg>
	);
}
